import { useCallback, useRef, useState } from 'react';
import { recordTurntable, pickMime, extensionFor } from '../lib/turntable';
import { downloadBlob } from '../lib/export';

const safeName = (s) => String(s || 'subsurface-3d').replace(/[^\w.-]+/g, '_');

/**
 * Turntable video of the 3D scene (manual 6.7): start(durationS) records the canvas of `viewerRef` (SubsurfaceViewer3D's
 * handle) while the camera makes one turn, then downloads `<name>-turntable.mp4` (`.webm` where the browser cannot record MP4).
 * state: { recording, elapsed, duration, error }.
 */
export default function useTurntableExport({ viewerRef, name, recordFn = recordTurntable, download = downloadBlob }) {
  const [state, setState] = useState({ recording: false, elapsed: 0, duration: 0, error: null });
  const abort = useRef(null);
  const supported = pickMime() !== null;

  const start = useCallback(async (durationS = 12) => {
    const viewer = viewerRef.current;
    const canvas = viewer?.getCanvas?.();
    if (!canvas || abort.current) return;
    abort.current = new AbortController();
    setState({ recording: true, elapsed: 0, duration: durationS, error: null });
    try {
      const { blob, mime } = await recordFn({
        canvas, durationS, signal: abort.current.signal,
        getPose: () => viewer.getCameraPose(), setPose: (p) => viewer.setCameraPose(p),
        onProgress: (elapsed) => setState((s) => ({ ...s, elapsed })),
      });
      download(blob, `${safeName(name)}-turntable.${extensionFor(mime || blob?.type)}`);
      setState({ recording: false, elapsed: 0, duration: 0, error: null });
    } catch (err) {
      setState({ recording: false, elapsed: 0, duration: 0, error: err?.name === 'AbortError' ? null : (err?.message || 'Recording failed') });
    } finally {
      abort.current = null;
    }
  }, [viewerRef, name, recordFn, download]);

  const cancel = useCallback(() => { abort.current?.abort(); }, []);
  return { ...state, supported, start, cancel };
}
